import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Scania from '../assets/img/scania.svg';
import { Link } from 'react-router-dom';
import { FaDiscord, FaInstagram, FaTruck, FaTwitch, FaVk, FaYoutube, FaRocket, FaStar } from 'react-icons/fa';

const Header = () => {
  const { t } = useTranslation();
  const heroRef = useRef();
  const textRef = useRef();
  const imageRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    if (heroRef.current) observer.observe(heroRef.current);
    if (textRef.current) observer.observe(textRef.current);
    if (imageRef.current) observer.observe(imageRef.current);

    return () => observer.disconnect();
  }, []);

  return (
    <section className="hero-modern" ref={heroRef}>
      <div className="container">
        <div className="row align-items-center min-vh-100">
          <div className="col-lg-6" ref={textRef}>
            <div className="hero-content">
              {/* Premium badge */}
              <div className="premium-badge fade-in-up stagger-1">
                <FaStar className="me-2" />
                <span>Premium VTC Experience</span>
              </div>

              {/* Main title with advanced typography */}
              <h1 className="hero-title fade-in-up stagger-2">
                <span className="text-white">Mileage</span>
                <span className="text-glow gradient-text">Riot</span>
                <div className="title-decoration"></div>
              </h1>

              {/* Animated subtitle */}
              <div className="hero-subtitle fade-in-up stagger-3">
                <p className="lead">
                  <span className="highlight-text">Добро пожаловать в компанию Mileage Riot!</span>
                  <br />
                  <span className="typing-text">Заинтересованы? Присоединяйтесь к нам!</span>
                </p>
              </div>

              {/* Enhanced action buttons */}
              <div className="hero-actions fade-in-up stagger-4">
                <a 
                  href="https://apply.mileageriot.com" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-hero-primary pulse-glow"
                >
                  <FaRocket className="me-2" />
                  <span>ВСТУПИТЬ</span>
                  <div className="btn-shine"></div>
                </a>
                
                <a 
                  href="https://discord.mileageriot.com" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-hero-secondary glass-container"
                >
                  <FaDiscord className="me-2" />
                  <span>DISCORD</span>
                </a>
              </div>

              {/* Social media with modern styling */}
              <div className="social-links fade-in-up stagger-5">
                <div className="social-title">
                  <span>FOLLOW OUR JOURNEY</span>
                  <div className="social-line"></div>
                </div>
                <div className="social-icons">
                  <a 
                    href="https://truckersmp.com/vtc/48336-mileageriot" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="social-icon tmp-icon float-animation"
                    style={{ animationDelay: '0s' }}
                  >
                    <FaTruck />
                    <span className="tooltip">TruckersMP</span>
                  </a>
                  <a 
                    href="https://vk.com/mileageriot" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="social-icon vk-icon float-animation"
                    style={{ animationDelay: '0.1s' }}
                  >
                    <FaVk />
                    <span className="tooltip">VKontakte</span>
                  </a>
                  <a 
                    href="https://www.youtube.com/@mileageriot_official" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="social-icon youtube-icon float-animation"
                    style={{ animationDelay: '0.2s' }}
                  >
                    <FaYoutube />
                    <span className="tooltip">YouTube</span>
                  </a>
                  <a 
                    href="https://twitch.tv/mileageriot_official" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="social-icon twitch-icon float-animation"
                    style={{ animationDelay: '0.3s' }}
                  >
                    <FaTwitch />
                    <span className="tooltip">Twitch</span>
                  </a>
                  <a 
                    href="https://www.instagram.com/mileageriot_official/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="social-icon instagram-icon float-animation"
                    style={{ animationDelay: '0.4s' }}
                  >
                    <FaInstagram />
                    <span className="tooltip">Instagram</span>
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-6" ref={imageRef}>
            <div className="hero-image fade-in-right stagger-3">
              <div className="image-container">
                <img 
                  src={Scania} 
                  className="hero-truck"
                  alt="Mileage Riot Truck" 
                  loading="lazy"
                />
                <div className="image-glow"></div>
                <div className="image-particles">
                  {[...Array(8)].map((_, i) => (
                    <div key={i} className={`img-particle img-particle-${i + 1}`}></div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="scroll-indicator">
        <div className="scroll-arrow"></div>
        <span>Scroll to explore</span>
      </div>
    </section>
  );
};

export default Header;